import React, { useCallback, useEffect, useRef, useState } from "react";
import { AvahanaMark } from "../ui/avahana-mark";
import { AvahanaWordmark } from "../ui/avahana-wordmark";
import "./brand-splash.css";

/**
 * Once-per-session brand splash.
 *
 * The mark traces itself on, the fill floods up through the finished outline,
 * the wordmark writes left to right, and then the whole lockup FLIPs onto
 * whatever `[data-brand-lockup]` is on the page — the sidebar in the app, the
 * card header on the login screen. The splash does not get dismissed; it
 * *becomes* the header.
 *
 * Discipline (all deliberate):
 *  - once per session, via sessionStorage
 *  - removed from the DOM afterwards, never left invisible intercepting clicks
 *  - the page renders underneath; nothing waits on the animation
 *  - skipped entirely under prefers-reduced-motion
 *  - repeat visits hidden by a pre-paint flag in index.html, not in React, so
 *    returning visitors get no one-frame flash
 */

const SCALE_UP = 2.6;

/** Single source of truth. CSS reads these as custom properties. */
const T = {
  trace: 700,
  traceDelay: 100,
  traceOut: 280,
  flood: 500,
  floodDelay: 700,
  // the word is drawn letter by letter: 7 glyphs, each starting `wStagger`
  // after the last, so the line visibly travels across the word
  wTrace: 420,
  wTraceDelay: 750,
  wStagger: 70,
  wFlood: 300,
  wFloodDelay: 1150,
  wTraceOut: 250,
  hold: 220,
  flight: 760,
  cover: 900,
};
const GLYPHS = 7;
const WORD_DRAWN = T.wTraceDelay + (GLYPHS - 1) * T.wStagger + T.wTrace;
const WORD_INKED = T.wFloodDelay + (GLYPHS - 1) * T.wStagger + T.wFlood;

/**
 * When the lockup starts flying to its target.
 *
 * The whole lockup has to be finished before it travels, so this is the later
 * of the two word passes — the outline being drawn and the fill flooding up
 * through it — plus the deliberate beat (`T.hold`) where the finished lockup
 * sits still before it moves. Taking `max` rather than assuming the flood
 * finishes last keeps this correct if the two timings are ever retuned
 * independently.
 */
const FLIGHT_AT = Math.max(WORD_DRAWN, WORD_INKED) + T.hold;

const cssVars: React.CSSProperties = {
  ["--bs-trace" as string]: `${T.trace}ms`,
  ["--bs-trace-delay" as string]: `${T.traceDelay}ms`,
  ["--bs-trace-out" as string]: `${T.traceOut}ms`,
  ["--bs-trace-out-delay" as string]: `${T.floodDelay + T.flood - 80}ms`,
  ["--bs-flood" as string]: `${T.flood}ms`,
  ["--bs-flood-delay" as string]: `${T.floodDelay}ms`,
  ["--bs-wtrace" as string]: `${T.wTrace}ms`,
  ["--bs-wflood" as string]: `${T.wFlood}ms`,
  ["--bs-wtrace-out" as string]: `${T.wTraceOut}ms`,
  ["--bs-wtrace-out-delay" as string]: `${WORD_INKED - 120}ms`,
  ["--bs-flight" as string]: `${T.flight}ms`,
  ["--bs-cover" as string]: `${T.cover}ms`,
};

export const SPLASH_SESSION_KEY = "avahana:splash-seen";

export function BrandSplash() {
  // Mirrors the pre-paint flag set in index.html. Gating on the ATTRIBUTE (not
  // on the sessionStorage flag this effect writes) is what makes the effect
  // safely re-runnable under StrictMode's double-mount: the first pass's
  // cleanup cancels its timers, and the second pass re-reads a flag that is
  // still set, so it reschedules instead of bailing out and stranding the
  // splash on screen forever.
  const [active, setActive] = useState(
    () =>
      typeof document !== "undefined" &&
      document.documentElement.hasAttribute("data-splashing")
  );
  const [flying, setFlying] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [transform, setTransform] = useState<string | null>(null);
  const lockupRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLSpanElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  /**
   * Open the aperture: grow a transparent hole in the backdrop so the page is
   * revealed through an expanding circle rather than by a fade.
   *
   * Driven from JS rather than by a CSS class for one reason — it returns a
   * handle. A class-triggered CSS animation that fails to apply (an unresolved
   * var in the shorthand, an unregistered custom property) does nothing and
   * says nothing, which is exactly how the first two attempts at this failed
   * silently. Here we either get an Animation object or we take the fallback.
   */
  const openAperture = useCallback(() => {
    const bd = backdropRef.current;
    if (!bd) return;

    // An expanding circular HOLE in the cover, so the page is revealed through
    // a growing aperture instead of a fade.
    //
    // Three techniques were tried and measured before this one. Both of the
    // obvious ones fail SILENTLY — the animation reports playState "running"
    // while nothing on screen moves:
    //   * box-shadow iris + animated width — keyframes resolved (0 -> 5715px)
    //     but the computed width never advanced.
    //   * radial-gradient mask + @property --iris — mask-image is a DISCRETE
    //     property, so Chrome will not interpolate it at all.
    //   * clip-path: path() — Chrome rejected the interpolation too.
    // clip-path: polygon() does interpolate, provided BOTH keyframes have the
    // same vertex count. So the hole is a fixed 48-gon joined to the outer
    // rect by a zero-width seam, and only its radius changes. Verified in the
    // browser: radius tweens linearly across the timeline.
    const w = window.innerWidth;
    const h = window.innerHeight;
    const cx = w / 2;
    const cy = h / 2;
    const rMax = Math.hypot(w, h) / 2 + 4; // must clear the far corners
    const SIDES = 48;

    const frame = (r: number) => {
      const pts: Array<[number, number]> = [[0, 0], [w, 0], [w, h], [0, h], [0, cy]];
      for (let i = 0; i <= SIDES; i++) {
        const a = Math.PI - (2 * Math.PI * i) / SIDES;
        pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
      }
      pts.push([0, cy], [0, 0]);
      return (
        "polygon(evenodd, " +
        pts.map(([x, y]) => `${x.toFixed(1)}px ${y.toFixed(1)}px`).join(", ") +
        ")"
      );
    };

    try {
      const anim = bd.animate(
        [{ clipPath: frame(0) }, { clipPath: frame(rMax) }] as Keyframe[],
        {
          duration: T.cover,
          easing: "cubic-bezier(0.22,0.61,0.36,1)",
          fill: "forwards",
        }
      );
      if (anim) return;
    } catch {
      /* fall through */
    }
    // Last resort: a plain fade, so the cover always clears.
    bd.style.transition = `opacity ${T.cover * 0.6}ms ease`;
    bd.style.opacity = "0";
  }, []);

  const finish = useCallback(() => {
    document.documentElement.removeAttribute("data-splashing");
    document.documentElement.removeAttribute("data-splashing-landing");
    document.body.style.removeProperty("overflow");
    try {
      sessionStorage.setItem(SPLASH_SESSION_KEY, "1");
    } catch {
      /* private mode — the splash simply runs again next load */
    }
    setActive(false);
  }, []);

  useEffect(() => {
    if (!active) return;
    document.body.style.overflow = "hidden";

    const timers: number[] = [];
    const at = (ms: number, fn: () => void) =>
      timers.push(window.setTimeout(fn, ms));

    at(FLIGHT_AT, () => {
      // Measure the MARK on both ends, not the lockup: only the mark travels,
      // because the sidebar rail has no room for the wordmark. The mark sits at
      // the lockup's left edge and shares its vertical centre, so a transform
      // applied to the lockup (origin: left center) moves the mark exactly as
      // these numbers describe.
      const from = markRef.current?.getBoundingClientRect();
      // Some routes render several lockups for responsive variants and hide all
      // but one. querySelector would happily return a display:none copy and the
      // mark would fly to a zero box, so pick the first one actually on screen.
      const target = Array.from(document.querySelectorAll("[data-brand-mark]"))
        .map((el) => el.getBoundingClientRect())
        .find((r) => r.width > 0 && r.height > 0);

      if (from && target) {
        // getBoundingClientRect returns the VISUAL box — already multiplied by
        // the SCALE_UP transform. Computing target.height / from.height would
        // double-count that scale. Recover the natural box first.
        // transform-origin: left center makes this exact, because the left
        // edge and the vertical centre do not move under scale.
        const naturalHeight = from.height / SCALE_UP;
        const naturalTop = from.top + from.height / 2 - naturalHeight / 2;
        const landScale = target.height / naturalHeight;
        const dx = target.left - from.left;
        const dy = target.top - naturalTop;
        setTransform(`translate(${dx}px, ${dy}px) scale(${landScale})`);
      } else {
        // No measurable target (route without a lockup) — fall back to a fade.
        setTransform(`scale(${SCALE_UP}) translateY(-6px)`);
      }
      setFlying(true);
      setLeaving(true);
      openAperture();
    });

    // Cross-fade the real lockup in while the splash copy is still arriving.
    // Overlapping them is what makes the hand-off invisible — waiting until
    // the flight ends produces a visible blink as one swaps for the other.
    at(FLIGHT_AT + T.flight * 0.65, () =>
      document.documentElement.setAttribute("data-splashing-landing", "")
    );

    at(FLIGHT_AT + Math.max(T.flight, T.cover) + 60, finish);

    return () => timers.forEach(clearTimeout);
  }, [active, finish, openAperture]);

  if (!active) return null;

  return (
    <div
      className={`brand-splash${flying ? " brand-splash--flying" : ""}${
        leaving ? " brand-splash--leaving" : ""
      }`}
      style={cssVars}
      aria-hidden="true"
    >
      <div ref={backdropRef} className="brand-splash__backdrop" />
      <div className="brand-splash__iris" />
      <div
        ref={lockupRef}
        className="brand-splash__lockup"
        style={{ transform: transform ?? `scale(${SCALE_UP})` }}
      >
        <span ref={markRef} className="brand-splash__mark">
          {/* Same path rendered twice: a stroke copy that draws itself on, and
              a fill copy that floods up through the finished outline. The
              outline is the mark's own construction, briefly made visible. */}
          <AvahanaMark size={26} className="brand-mark-flood" />
          <AvahanaMark size={26} className="brand-mark-trace" />
        </span>
        <span className="brand-splash__word">
          {/* Real glyph outlines, one path per letter, so the word is DRAWN
              left to right — the same treatment as the mark, not a wipe. */}
          <AvahanaWordmark
            height={18}
            className="brand-word-flood"
            glyphClassName="brand-word-flood-glyph"
            stagger={T.wStagger / 1000}
            delay={T.wFloodDelay / 1000}
          />
          <AvahanaWordmark
            height={18}
            className="brand-splash__word-trace brand-word-trace"
            stagger={T.wStagger / 1000}
            delay={T.wTraceDelay / 1000}
          />
        </span>
      </div>
    </div>
  );
}
